import { atom } from 'nanostores';
import { environmentTypeOptions } from '@/data/environmentTypes';
import { styleOptions } from '@/data/styles';
import { WindowType } from '@/enums/WindowType';

const Settings = {
  maxCommentLength: 2000,
  maxLogoSize: 5 * 1024 * 1024,
  logoExtensions: ['svg', 'png', 'jpg', 'jpeg', 'webp'],
  errors: {
    style: 'Choose an interior style to continue.',
    threeStyles: 'Choose three interior styles to continue.',
    uniqueStyles: 'Choose three different styles. Each style can only be used once.',
    view: 'Choose a window view to continue.',
    customView: 'Describe your custom window view, or choose a listed view.',
    customViewLength: 'Keep your custom view within 2,000 characters.',
    window: 'Choose Panoramic or Standard windows.',
    website: 'Enter a website such as yourcompany.com, or leave this optional field blank.',
    logoType: 'Choose an SVG, PNG, JPG or WebP logo.',
    logoEmpty: 'This logo file is empty. Choose another file.',
    logoSize: 'This logo is larger than 5 MB. Choose a smaller file.',
    logoImage: 'This image could not be opened. Export it again or choose another file.',
    comment: 'Keep your request within 2,000 characters.',
    checking: 'Checking your logo… Please wait.',
  },
};

const en = {
  label: 'Design & requests',
  title: 'Design & requests',
};

const getExtension = (file) => file.name.split('.').pop()?.toLowerCase() || '';

export class Step2 {
  id = '2';

  locale = { en, ru: en };

  $styles = atom(['']);

  $environmentType = atom('');

  $customView = atom('');

  $windowType = atom('');

  $website = atom('');

  $logo = atom(null);

  $logoPreview = atom('');

  $isLogoChecking = atom(false);

  $comment = atom('');

  $hasEnvironmentType = atom(false);

  $hasBranding = atom(false);

  $styleError = atom('');

  $environmentError = atom('');

  $windowError = atom('');

  $websiteError = atom('');

  $logoError = atom('');

  $commentError = atom('');

  logoRequestId = 0;

  store;

  constructor(store) {
    this.store = store;
  }

  onTariffChange(tariff) {
    this.onReset();
    this.$styles.set(new Array(tariff.styleCount || 1).fill(''));
    this.$hasEnvironmentType.set(Boolean(tariff.hasEnvironmentType));
    this.$hasBranding.set(Boolean(tariff.hasBranding));
  }

  onReset() {
    this.logoRequestId += 1;
    this.$isLogoChecking.set(false);
    const preview = this.$logoPreview.get();
    if (preview) URL.revokeObjectURL(preview);
    this.$styles.set(['']);
    this.$environmentType.set('');
    this.$customView.set('');
    this.$windowType.set('');
    this.$website.set('');
    this.$logo.set(null);
    this.$logoPreview.set('');
    this.$comment.set('');
    this.$hasEnvironmentType.set(false);
    this.$hasBranding.set(false);
    this.clearErrors();
  }

  async onValidate() {
    if (this.$isLogoChecking.get()) {
      this.$logoError.set(Settings.errors.checking);
      this.store.orderStore.focusField('logo');
      return false;
    }
    const styles = this.$styles.get();
    const isMultiple = styles.length === 3;
    const expectedCount = this.store.orderStore.$tariff.get()?.styleCount || 1;
    const missingStyles = styles.length !== expectedCount
      || styles.some((style) => !styleOptions.en.some((option) => option.value === style));
    const duplicateStyles = new Set(styles.filter(Boolean)).size !== styles.filter(Boolean).length;
    const environmentType = this.$environmentType.get();
    const hasEnvironmentType = this.$hasEnvironmentType.get();
    const windowType = this.$windowType.get();

    let styleError = '';
    if (missingStyles) styleError = isMultiple ? Settings.errors.threeStyles : Settings.errors.style;
    this.$styleError.set(styleError);
    if (!missingStyles && isMultiple && duplicateStyles) this.$styleError.set(Settings.errors.uniqueStyles);
    const validEnvironment = environmentTypeOptions.en.some((option) => option.value === environmentType);
    this.$environmentError.set(hasEnvironmentType && !validEnvironment ? Settings.errors.view : '');
    if (environmentType === 'custom' && !this.$customView.get().trim()) this.$environmentError.set(Settings.errors.customView);
    if (environmentType === 'custom' && this.$customView.get().trim().length > Settings.maxCommentLength) {
      this.$environmentError.set(Settings.errors.customViewLength);
    }
    this.$windowError.set(!Object.values(WindowType).includes(windowType) ? Settings.errors.window : '');
    this.validateWebsite();
    this.$commentError.set(this.$comment.get().length > Settings.maxCommentLength ? Settings.errors.comment : '');

    const errorFields = [
      ['styles', this.$styleError.get()],
      ['environment', this.$environmentError.get()],
      ['window', this.$windowError.get()],
      ['website', this.$websiteError.get()],
      ['logo', this.$logoError.get()],
      ['comment', this.$commentError.get()],
    ];
    const firstError = errorFields.find(([, error]) => error);
    if (firstError) this.store.orderStore.focusField(firstError[0]);
    return !firstError;
  }

  clearErrors() {
    this.$styleError.set('');
    this.$environmentError.set('');
    this.$windowError.set('');
    this.$websiteError.set('');
    this.$logoError.set('');
    this.$commentError.set('');
  }

  setStyle(value, index) {
    const styles = [...this.$styles.get()];
    styles[index] = value;
    this.$styles.set(styles);
    this.$styleError.set('');
    this.store.orderStore.scheduleDraftSave();
  }

  setEnvironmentType(value) {
    this.$environmentType.set(value);
    this.$environmentError.set('');
    this.store.orderStore.scheduleDraftSave();
  }

  setCustomView(value) {
    this.$customView.set(value);
    this.$environmentError.set('');
    this.store.orderStore.scheduleDraftSave();
  }

  setWindowType(value) {
    this.$windowType.set(value);
    this.$windowError.set('');
    this.store.orderStore.scheduleDraftSave();
  }

  setWebsite(value) {
    this.$website.set(value);
    this.$websiteError.set('');
    this.store.orderStore.scheduleDraftSave();
  }

  validateWebsite() {
    const website = this.$website.get().trim();
    if (!website) {
      this.$websiteError.set('');
      return true;
    }
    const isValid = website.length <= 2048 && /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}([/?#].*)?$/i.test(website);
    this.$websiteError.set(isValid ? '' : Settings.errors.website);
    return isValid;
  }

  async setLogo(file) {
    this.logoRequestId += 1;
    const requestId = this.logoRequestId;
    this.$isLogoChecking.set(Boolean(file));
    const currentPreview = this.$logoPreview.get();
    if (currentPreview) URL.revokeObjectURL(currentPreview);
    if (!file) {
      this.$logo.set(null);
      this.$logoPreview.set('');
      this.$logoError.set('');
      this.store.orderStore.scheduleDraftSave();
      return;
    }

    const extension = getExtension(file);
    let error = '';
    if (!Settings.logoExtensions.includes(extension)) error = Settings.errors.logoType;
    else if (file.size === 0) error = Settings.errors.logoEmpty;
    else if (file.size > Settings.maxLogoSize) error = Settings.errors.logoSize;
    else {
      try {
        if (extension !== 'svg') {
          const bitmap = await window.createImageBitmap(file);
          bitmap.close();
        } else {
          const contents = await file.text();
          if (!contents.includes('<svg')) error = Settings.errors.logoImage;
        }
      } catch (exception) {
        error = Settings.errors.logoImage;
      }
    }

    if (requestId !== this.logoRequestId) return;
    this.$isLogoChecking.set(false);
    this.$logo.set(error ? null : file);
    this.$logoPreview.set(error ? '' : URL.createObjectURL(file));
    this.$logoError.set(error);
    this.store.orderStore.scheduleDraftSave();
  }

  setComment(value) {
    this.$comment.set(value);
    this.$commentError.set(value.length > Settings.maxCommentLength ? Settings.errors.comment : '');
    this.store.orderStore.scheduleDraftSave();
  }

  getDraftData() {
    return {
      styles: this.$styles.get(),
      environmentType: this.$environmentType.get(),
      customView: this.$customView.get(),
      windowType: this.$windowType.get(),
      website: this.$website.get(),
      logo: this.$logo.get(),
      comment: this.$comment.get(),
    };
  }

  async restoreDraft(data = {}) {
    if (Array.isArray(data.styles) && data.styles.length) this.$styles.set(data.styles);
    this.$environmentType.set(data.environmentType || '');
    this.$customView.set(data.customView || '');
    this.$windowType.set(data.windowType || '');
    this.$website.set(data.website || '');
    this.$comment.set(data.comment || '');
    this.clearErrors();
    await this.setLogo(data.logo || null);
  }
}
